import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PriorityBadge, StatusBadge, CategoryBadge } from "../components/Badges";
import { PRIORITY_LABEL, CATEGORY_LABEL, STATUS_LABEL, relTime, slaCountdown } from "../lib/labels";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { Clock, Warning, CheckCircle, HourglassMedium, Ticket as TicketIcon } from "@phosphor-icons/react";
import InstallPrompt from "../components/InstallPrompt";
import ChatAnalyticsCard from "../components/ChatAnalyticsCard";
import ClientRepliesWidget from "../components/ClientRepliesWidget";

const COLORS = ["hsl(0,84%,55%)", "hsl(28,95%,53%)", "hsl(222,84%,45%)", "hsl(222,12%,50%)"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    const [s, t, e, w] = await Promise.all([
      api.get("/stats/overview"),
      api.get("/tickets", { params: { page: 1, limit: 8 } }),
      api.get("/stats/tech-emails").catch(() => ({ data: [] })),
      api.get("/stats/open-rate-weekly").catch(() => ({ data: [] })),
    ]);
    setStats({ ...s.data, emails: e.data, open_weekly: w.data });
    const list = Array.isArray(t.data) ? t.data : (t.data.items || []);
    setRecent(list.slice(0, 8));
  };

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  if (!stats) return <div className="p-8 text-muted-foreground">Chargement…</div>;

  const prioData = ["critique", "urgente", "normale", "faible"].map((k) => ({
    name: PRIORITY_LABEL[k], value: stats.by_priority[k] || 0,
  }));

  const catData = Object.entries(stats.by_category || {}).map(([k, v]) => ({
    name: CATEGORY_LABEL[k] || k, value: v,
  }));

  return (
    <div className="space-y-8">
      <InstallPrompt/>
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Vue d'ensemble</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Tableau de bord</h1>
        </div>
        <div className="text-sm text-muted-foreground">Mise à jour automatique toutes les 30 s</div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard title="Tickets ouverts" value={stats.open} icon={<TicketIcon size={20} weight="fill"/>} tone="primary"/>
        <MetricCard title="Urgents / Critiques" value={stats.urgent} icon={<Warning size={20}/>} tone="critical"/>
        <MetricCard title="En attente" value={stats.waiting} icon={<HourglassMedium size={20}/>} tone="warn"/>
        <MetricCard title="Résolus aujourd'hui" value={stats.resolved_today} icon={<CheckCircle size={20}/>} tone="good"/>
      </div>

      <ClientRepliesWidget/>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="font-display">Volume — 14 derniers jours</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={stats.volume}>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="hsl(222,84%,45%)" strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display">Par priorité</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={prioData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}>
                  {prioData.map((_, i) => <Cell key={i} fill={COLORS[i]}/>)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1 text-xs mt-2">
              {prioData.map((p, i) => (
                <div key={p.name} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: COLORS[i] }}/>
                  {p.name} · <span className="mono">{p.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-display">Par catégorie</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={catData}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60}/>
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
                <Tooltip />
                <Bar dataKey="value" fill="hsl(222,84%,45%)" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-display">Taux d'ouverture e-mails — 8 dernières semaines</CardTitle>
          </CardHeader>
          <CardContent>
            {(stats.open_weekly || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Pas encore assez de données.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.open_weekly}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }}/>
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%"/>
                  <Tooltip formatter={(v, k) => k === "open_rate" ? `${v}%` : v}/>
                  <Line type="monotone" dataKey="open_rate" stroke="hsl(142,71%,40%)" strokeWidth={2} dot={{ r: 3 }} name="Taux d'ouverture"/>
                  <Line type="monotone" dataKey="sent" stroke="hsl(222,84%,45%)" strokeWidth={1} strokeDasharray="4 4" dot={false} name="E-mails envoyés"/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-3">
          <CardHeader><CardTitle className="font-display">Performance techniciens</CardTitle></CardHeader>
          <CardContent>
            {stats.techs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun ticket assigné pour l'instant.</p>
            ) : (
              <table className="w-full text-sm" data-testid="tech-perf-table">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left py-2">Technicien</th>
                    <th className="text-right py-2">Total</th>
                    <th className="text-right py-2">Résolus</th>
                    <th className="text-right py-2">Taux</th>
                    <th className="text-right py-2">E-mails ouverts</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.techs.map((t) => {
                    const em = (stats.emails || []).find((e) => e.id === t.id) || { sent: 0, opened: 0, open_rate: 0 };
                    return (
                      <tr key={t.id} className="border-t">
                        <td className="py-2 font-medium">{t.name}</td>
                        <td className="py-2 text-right mono">{t.total}</td>
                        <td className="py-2 text-right mono">{t.resolved}</td>
                        <td className="py-2 text-right mono">{t.total ? Math.round((t.resolved / t.total) * 100) : 0}%</td>
                        <td className="py-2 text-right mono">{em.opened}/{em.sent} · <b className={em.open_rate >= 60 ? "text-emerald-600" : em.open_rate >= 30 ? "text-orange-500" : "text-muted-foreground"}>{em.open_rate}%</b></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display">Derniers tickets</CardTitle>
          <Link to="/tickets" className="text-sm text-primary hover:underline" data-testid="see-all-tickets">Tout voir →</Link>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            {recent.map((t) => {
              const sla = slaCountdown(t.sla_resolution_deadline);
              return (
                <Link key={t.id} to={`/tickets/${t.id}`}
                  data-testid={`recent-ticket-${t.number}`}
                  className="grid grid-cols-12 gap-3 py-3 items-center hover:bg-muted/40 -mx-4 px-4 transition-colors">
                  <div className="col-span-1 mono text-muted-foreground flex items-center gap-1.5">
                    {t.is_read === false && (
                      <span
                        data-testid={`recent-unread-dot-${t.number}`}
                        title="Nouvelle réponse à lire"
                        className="inline-block w-2 h-2 rounded-full bg-blue-500 shrink-0"
                      />
                    )}
                    #{t.number}
                  </div>
                  <div className="col-span-5">
                    <div className={`truncate ${t.is_read === false ? "font-bold" : "font-medium"}`}>{t.subject}</div>
                    <div className="text-xs text-muted-foreground">{t.client_name} · {relTime(t.created_at)}</div>
                  </div>
                  <div className="col-span-2"><PriorityBadge value={t.priority}/></div>
                  <div className="col-span-2"><StatusBadge value={t.status}/></div>
                  <div className={`col-span-2 text-xs text-right mono ${sla.breached ? "text-destructive font-semibold" : sla.urgent ? "text-orange-500" : "text-muted-foreground"}`}>
                    <Clock size={12} className="inline mr-1"/>{sla.text}
                  </div>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChatAnalyticsCard/>
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon, tone }) {
  const tones = {
    primary: "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
    critical: "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300",
    warn: "bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",
    good: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  };
  return (
    <Card className="card-hover">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-caps">{title}</div>
            <div className="font-display text-4xl font-bold mt-1">{value}</div>
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}
